import { useState, useEffect } from 'react';
import { getCalculationData, getCostVariables, PRICING_UNAVAILABLE_MESSAGE } from '../apiCalls';

export const buildPanelOptions = (config) => {
    if (!config || typeof config !== 'object' || Array.isArray(config)
        || config.code || config.error || config.success === false || config.isSuccess === false) {
        throw new Error(PRICING_UNAVAILABLE_MESSAGE);
    }
    const { panelSize, minPanels, maxPanels } = config;
    if (!Number.isFinite(panelSize) || panelSize <= 0
        || !Number.isInteger(minPanels) || !Number.isInteger(maxPanels)
        || minPanels <= 0 || maxPanels < minPanels || maxPanels > 10000) {
        throw new Error(PRICING_UNAVAILABLE_MESSAGE);
    }
    const size = panelSize;
    const options = [];
    for (let i = minPanels; i <= maxPanels; i++) {
        options.push({ value: i, label: `${i} panels ${(size * i) / 1000}kWp` });
    }
    return options;
};

export const hasAddonConfiguration = (addons) => Array.isArray(addons)
    && addons.every(addon => addon && typeof addon === 'object'
        && typeof addon.key === 'string' && addon.key.trim().length > 0
        && Number.isFinite(addon.order));

export const useInitialData = () => {
    const [panelOptions, setPanelOptions] = useState([]);
    const [addons, setAddons] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const [calcResponse, addonsResponse] = await Promise.all([
                    getCalculationData(),
                    getCostVariables(),
                ]);

                if (cancelled) return;

                const options = buildPanelOptions(calcResponse.data || {});
                if (!hasAddonConfiguration(addonsResponse.data)) {
                    throw new Error(PRICING_UNAVAILABLE_MESSAGE);
                }
                setPanelOptions(options);
                setAddons([...addonsResponse.data].sort((a, b) => a.order - b.order));

                // locale is handled by the widget, not solmatix-client
            } catch (err) {
                if (!cancelled) {
                    setPanelOptions([]);
                    setAddons([]);
                    setError(`${PRICING_UNAVAILABLE_MESSAGE} ${typeof err?.friendlyMessage === 'string' && err.friendlyMessage.trim() ? err.friendlyMessage : 'Unable to load current configuration.'}`);
                }
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };

        load();
        return () => { cancelled = true; };
    }, []);

    return { panelOptions, addons, isLoading, error };
};
